'use client';

import { useState, useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { supabaseBrowser } from '../../../lib/supabaseBrowser';
import { safeNext } from '@/lib/pendingEntry';
import Arrow from '@/app/customer-homepage/components/Arrow';

const supabase = supabaseBrowser;


export default function RegisterForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const referralCode = searchParams?.get('ref');
  const next = safeNext(searchParams?.get('next'), '');
  const loginHref = next ? `/customer-login?next=${encodeURIComponent(next)}` : '/customer-login';

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    phone: '',
    address: '',
    town: '',
    county: '',
    postcode: '',
    country: 'United Kingdom',
    referralCode: referralCode || '',
    marketingOptIn: false,
  });

  useEffect(() => {
    if (referralCode) {
      setFormData(prev => ({ ...prev, referralCode }));
    }
  }, [referralCode]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setError('');
  };

  // Calculate age from date of birth
  const calculateAge = (dateOfBirth) => {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today?.getFullYear() - birthDate?.getFullYear();
    const monthDiff = today?.getMonth() - birthDate?.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today?.getDate() < birthDate?.getDate())) {
      age--;
    }
    
    return age;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setLoading(true);

    // Validation
    if (formData?.password !== formData?.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (formData?.password?.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    // Date of birth validation
    if (!formData?.dateOfBirth) {
      setError('Date of birth is required');
      setLoading(false);
      return;
    }

    let age = calculateAge(formData?.dateOfBirth);
    if (age < 18) {
      setError('You must be at least 18 years old to register');
      setLoading(false);
      return;
    }

    try {
      // Sign up user
      const { data: authData, error: signUpError } = await supabase?.auth?.signUp({
        email: formData?.email,
        password: formData?.password,
        options: {
          ...(next && typeof window !== 'undefined'
            ? { emailRedirectTo: `${window.location.origin}/auth/confirm?next=${encodeURIComponent(next)}` }
            : {}),
          data: {
            first_name: formData?.firstName,
            last_name: formData?.lastName,
            full_name: `${formData?.firstName} ${formData?.lastName}`,
            date_of_birth: formData?.dateOfBirth,
            phone: formData?.phone,
            address: formData?.address,
            city: formData?.town,        // Map town to city
            state: formData?.county,     // Map county to state
            zip_code: formData?.postcode, // Map postcode to zip_code
            country: formData?.country,
            referral_code_used: formData?.referralCode || null
          }
        }
      });

      if (signUpError) throw signUpError;

      // A referral code is linked by the database when the account is created
      // (from referral_code_used above), so it works even before the email is
      // confirmed and there's no session yet. If a session does exist, this
      // call is a harmless no-op for the player's own account.
      if (formData?.referralCode && authData?.user?.id && authData?.session) {
        const { error: referralError } = await supabase
          ?.rpc('create_referral', {
            referrer_code: formData?.referralCode,
            new_user_id: authData?.user?.id
          });

        if (referralError) {
          console.error('Error creating referral:', referralError);
          // Don't fail registration if referral creation fails
        }
      }

      // Supabase Auth sends the (branded) confirmation email itself via the
      // configured Resend SMTP + the "Confirm your account" template. We no
      // longer send a second custom verification email — it duplicated the
      // Supabase one and pointed at a non-existent /verify-email route.

      // Track Signed Up in Klaviyo and subscribe to marketing list (non-fatal)
      try {
        await fetch('/api/klaviyo/track', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            event: 'Signed Up',
            properties: {
              referral_code_used: formData?.referralCode || null,
              marketingOptIn: formData?.marketingOptIn,
            },
            profile: {
              email: formData?.email,
              external_id: authData?.user?.id,
              first_name: formData?.firstName,
              last_name: formData?.lastName,
              phone_number: formData?.phone || undefined,
            },
          }),
        });
      } catch (klaviyoError) {
        console.error('Klaviyo Signed Up error:', klaviyoError);
      }

      setSuccess(true);
      setTimeout(() => {
        router?.push(loginHref);
      }, 2000);

    } catch (err) {
      console.error('Registration error:', err);
      setError(err?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const labelCls = 'block text-sm font-semibold text-foreground mb-1.5';
  const inputCls =
    'w-full rounded-[6px] border border-white/[0.12] bg-black/30 px-3 py-3 text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent';

  if (success) {
    return (
      <section className="bg-background">
        <div className="max-w-[1520px] mx-auto px-[var(--gutter)] py-[clamp(2.5rem,6vw,5rem)]">
          <div className="max-w-[34rem] rounded-[8px] border border-accent bg-[#111612] p-5 sm:p-6">
            <div className="w-12 h-12 bg-accent rounded-full flex items-center justify-center mb-5">
              <svg className="w-7 h-7 text-accent-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.4} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="d text-[length:var(--t-h3)] mb-3">Account created.</h2>
            <p className="text-foreground/85 mb-3">
              Please check your email to confirm your account.
            </p>
            <p className="text-sm text-muted-foreground">
              Taking you to the log-in page…
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-background">
      <div className="max-w-[1520px] mx-auto px-[var(--gutter)] py-[clamp(2.5rem,6vw,5rem)]">
        <div className="max-w-[40rem]">
          <h1 className="d text-[length:var(--t-h2)]">Create account.</h1>
          <p className="mt-4 mb-8 max-w-[40ch] text-[length:var(--t-lede)] leading-[1.45] text-muted-foreground">
            Join Rendalls Competitions and start entering today.
          </p>

          {referralCode && (
            <div className="mb-6 rounded-[6px] border border-accent bg-[rgba(50,216,0,0.10)] p-4">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 shrink-0 text-accent-bright" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <p className="text-sm text-accent-bright font-semibold">
                  You were referred. Get a £5 bonus on your first purchase.
                </p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="rounded-[8px] border border-white/10 bg-[#111612] p-5 sm:p-6 space-y-5">
            {error && (
              <div className="rounded-[6px] border border-[rgba(255,84,112,0.35)] bg-[rgba(255,84,112,0.10)] p-3 text-error text-sm">
                {error}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-4">
              <div>
                <label htmlFor="firstName" className={labelCls}>
                  First name *
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData?.firstName}
                  onChange={handleChange}
                  required
                  autoComplete="given-name"
                  className={inputCls}
                />
              </div>

              <div>
                <label htmlFor="lastName" className={labelCls}>
                  Last name *
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData?.lastName}
                  onChange={handleChange}
                  required
                  autoComplete="family-name"
                  className={inputCls}
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className={labelCls}>
                Email address *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData?.email}
                onChange={handleChange}
                required
                autoComplete="email"
                className={inputCls}
              />
            </div>

            <div>
              <label htmlFor="phone" className={labelCls}>
                Phone number
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData?.phone}
                onChange={handleChange}
                autoComplete="tel"
                className={inputCls}
              />
            </div>

            <div>
              <label htmlFor="dateOfBirth" className={labelCls}>
                Date of birth * <span className="text-xs font-normal text-muted-foreground">(you must be 18 or over)</span>
              </label>
              <input
                type="date"
                id="dateOfBirth"
                name="dateOfBirth"
                value={formData?.dateOfBirth}
                onChange={handleChange}
                required
                max={new Date(new Date().setFullYear(new Date().getFullYear() - 18))?.toISOString()?.split('T')?.[0]}
                className={`${inputCls} [color-scheme:dark]`}
              />
            </div>

            <div>
              <label htmlFor="address" className={labelCls}>
                Address *
              </label>
              <input
                type="text"
                id="address"
                name="address"
                value={formData?.address}
                onChange={handleChange}
                required
                placeholder="Street address"
                autoComplete="address-line1"
                className={inputCls}
              />
            </div>

            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label htmlFor="town" className={labelCls}>
                  Town or city *
                </label>
                <input
                  type="text"
                  id="town"
                  name="town"
                  value={formData?.town}
                  onChange={handleChange}
                  required
                  placeholder="Town"
                  autoComplete="address-level2"
                  className={inputCls}
                />
              </div>

              <div>
                <label htmlFor="county" className={labelCls}>
                  County *
                </label>
                <input
                  type="text"
                  id="county"
                  name="county"
                  value={formData?.county}
                  onChange={handleChange}
                  required
                  placeholder="County"
                  className={inputCls}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label htmlFor="postcode" className={labelCls}>
                  Postcode *
                </label>
                <input
                  type="text"
                  id="postcode"
                  name="postcode"
                  value={formData?.postcode}
                  onChange={handleChange}
                  required
                  placeholder="UK postcode"
                  autoComplete="postal-code"
                  className={inputCls}
                />
              </div>

              <div>
                <label htmlFor="country" className={labelCls}>
                  Country *
                </label>
                <input
                  type="text"
                  id="country"
                  name="country"
                  value={formData?.country}
                  disabled
                  className="w-full rounded-[6px] border border-white/[0.08] bg-white/[0.04] px-3 py-3 text-muted-foreground cursor-not-allowed"
                />
              </div>
            </div>

            <p className="text-xs text-muted-foreground -mt-2">
              Registration is open to UK residents only.
            </p>

            <div>
              <label htmlFor="password" className={labelCls}>
                Password *
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData?.password}
                onChange={handleChange}
                required
                minLength={6}
                autoComplete="new-password"
                className={inputCls}
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className={labelCls}>
                Confirm password *
              </label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData?.confirmPassword}
                onChange={handleChange}
                required
                minLength={6}
                autoComplete="new-password"
                className={inputCls}
              />
            </div>

            <div>
              <label htmlFor="referralCode" className={labelCls}>
                Referral code <span className="font-normal text-muted-foreground">(optional)</span>
              </label>
              <input
                type="text"
                id="referralCode"
                name="referralCode"
                value={formData?.referralCode}
                onChange={handleChange}
                placeholder="Enter referral code"
                className={inputCls}
              />
            </div>

            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="marketingOptIn"
                name="marketingOptIn"
                checked={formData?.marketingOptIn}
                onChange={handleChange}
                className="mt-1 h-4 w-4 shrink-0 accent-accent cursor-pointer"
              />
              <label htmlFor="marketingOptIn" className="text-sm text-muted-foreground cursor-pointer">
                Send me updates about new competitions and promotions (optional)
              </label>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-block btn-go w-full disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                'Creating account…'
              ) : (
                <>
                  Create account <Arrow />
                </>
              )}
            </button>

            <p className="text-sm text-muted-foreground">
              Already have an account?{' '}
              <Link href={loginHref} className="font-semibold text-accent-bright underline underline-offset-2">
                Log in
              </Link>
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}

RegisterForm.propTypes = {};
